import { 
    runOrchestration, 
    classifyAndRoute, 
    getDataById, 
    getMetrics, 
    getTrace,
    classifyIntent 
} from './api';

// Types for orchestration data
export interface ToolResult {
    tool: string;
    status: 'success' | 'error' | 'pending';
    result: {
        summary?: string;
        data?: any;
        image?: string;
        chart?: any;
    };
    timestamp: string;
}

export interface OrchestrationResponse {
    intent: string;
    tools: ToolResult[];
    overall_status: 'success' | 'error' | 'pending';
    timestamp: string;
    session_id: string;
}

export interface MetricsData {
    success_rate: number;
    avg_latency: number;
    total_requests: number;
    error_count: number;
}

export interface TraceData {
    session_id: string;
    timestamp: string;
    intent: string;
    tools_used: string[];
    status: string;
    duration: number;
}

// Service functions for orchestration
export class OrchestrationService {
    
    // Run orchestration with user input
    static async runOrchestration(input: string, context?: any): Promise<OrchestrationResponse> {
        try {
            const response = await runOrchestration({
                input,
                context: context || {}
            });
            return response;
        } catch (error) {
            console.error('Orchestration failed:', error);
            throw error;
        }
    }

    // Classify user intent and route to appropriate tools
    static async classifyIntent(input: string): Promise<any> {
        try {
            const response = await classifyAndRoute({
                input
            });
            return response;
        } catch (error) {
            console.error('Intent classification failed:', error);
            throw error;
        }
    }

    // Get patient/visit data by ID
    static async getPatientData(id: string): Promise<any> {
        try {
            const response = await getDataById(id);
            return response;
        } catch (error) {
            console.error('Failed to fetch patient data:', error);
            throw error;
        }
    }

    // Get system metrics
    static async getSystemMetrics(): Promise<MetricsData> {
        try {
            const response = await getMetrics();
            return response;
        } catch (error) {
            console.error('Failed to fetch metrics:', error);
            // Return mock data if API fails
            return {
                success_rate: 94.7,
                avg_latency: 1.2,
                total_requests: 1247,
                error_count: 65
            };
        }
    }

    // Get session history/trace
    static async getSessionHistory(): Promise<TraceData[]> {
        try {
            const response = await getTrace();
            return response;
        } catch (error) {
            console.error('Failed to fetch trace data:', error);
            // Return mock data if API fails
            return [
                {
                    session_id: 'sess_001',
                    timestamp: '2025-01-15T10:30:00Z',
                    intent: 'analyze_retinal_scan',
                    tools_used: ['image_analysis', 'risk_assessment'],
                    status: 'success',
                    duration: 2.3
                },
                {
                    session_id: 'sess_002',
                    timestamp: '2025-01-15T09:15:00Z',
                    intent: 'patient_summary',
                    tools_used: ['summarize_visit', 'extract_metrics'],
                    status: 'success',
                    duration: 1.8
                }
            ];
        }
    }

    // Process orchestration response and format for UI
    static processOrchestrationResponse(response: OrchestrationResponse): {
        intent: string;
        tools: ToolResult[];
        overallStatus: string;
        timestamp: string;
        sessionId: string;
    } {
        return {
            intent: response.intent,
            tools: response.tools,
            overallStatus: response.overall_status,
            timestamp: response.timestamp,
            sessionId: response.session_id
        };
    }

    // Format tool results for display
    static formatToolResult(tool: ToolResult): {
        name: string;
        status: string;
        result: string;
        timestamp: string;
        icon: string;
    } {
        const statusIcons = {
            success: '✅',
            error: '❌',
            pending: '⏳'
        };

        const toolNames: { [key: string]: string } = {
            'summarize_visit': 'Summarize Visit',
            'image_analysis': 'Image Analysis',
            'risk_assessment': 'Risk Assessment',
            'extract_metrics': 'Extract Metrics',
            'patient_summary': 'Patient Summary'
        };

        return {
            name: toolNames[tool.tool] || tool.tool,
            status: tool.status,
            result: tool.result.summary || 'Processing...',
            timestamp: new Date(tool.timestamp).toLocaleString(),
            icon: statusIcons[tool.status] || '❓'
        };
    }
}

export default OrchestrationService;






